import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank, CaretDown } from "@phosphor-icons/react";

function isoToDate(iso) {
  if (!iso) return null;
  // Construct as local date to avoid TZ shift (YYYY-MM-DD -> Date)
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function dateToIso(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatDisplay(iso) {
  if (!iso) return "";
  const d = isoToDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Date picker that only enables dates present in `availableDates` (ISO strings).
 * If `availableDates` is empty, *every* date is disabled and the trigger shows
 * a "Sin datos disponibles" hint.
 */
export default function AvailableDatePicker({
  value,
  onChange,
  availableDates = [],
  placeholder = "Seleccionar fecha…",
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);
  const minDate = availableDates.length ? isoToDate(availableDates[0]) : undefined;
  const maxDate = availableDates.length ? isoToDate(availableDates[availableDates.length - 1]) : undefined;
  const defaultMonth = value ? isoToDate(value) : (maxDate || new Date());

  const isDayDisabled = (date) => {
    if (!availableDates.length) return true;
    return !availableSet.has(dateToIso(date));
  };

  const hasData = availableDates.length > 0;
  const label = value && availableSet.has(value)
    ? formatDisplay(value)
    : (hasData ? placeholder : "Sin datos disponibles para este nivel");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || !hasData}
          data-testid={testId}
          className={`h-8 w-full px-2 rounded-none border border-white/10 bg-[#121212] text-xs font-mono flex items-center justify-between gap-2 ${
            disabled || !hasData ? "opacity-40 cursor-not-allowed" : "hover:border-white/30 cursor-pointer"
          }`}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <CalendarBlank size={12} className="text-white/40 shrink-0" />
            <span className={`truncate ${value && availableSet.has(value) ? "text-white" : "text-white/40"}`}>{label}</span>
          </span>
          <CaretDown size={10} className="text-white/30 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 bg-[#0A0A0A] border-white/10 rounded-none text-white w-auto"
      >
        <Calendar
          mode="single"
          selected={value ? isoToDate(value) : undefined}
          defaultMonth={defaultMonth}
          fromDate={minDate}
          toDate={maxDate}
          disabled={isDayDisabled}
          onSelect={(d) => {
            if (!d) return;
            onChange(dateToIso(d));
            setOpen(false);
          }}
          weekStartsOn={1}
          className="bg-[#0A0A0A]"
        />
        {hasData && (
          <div className="px-3 py-2 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
            {availableDates.length.toLocaleString("es-ES")} fechas disponibles · {formatDisplay(availableDates[0])} → {formatDisplay(availableDates[availableDates.length - 1])}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
